import { useEffect, useRef, useState } from 'react';
import storage from '../../../../../hooks/useStorage/useStorage';
import { StorageEnum } from '../../../../../modules/context/storage';
import { CollapsedMode, SidebarState } from '../appTemplate.types';

interface UseSidebarStateParams {
  collapsedMode: CollapsedMode;
  isNested: boolean;
  contextKey: string;
}

type StoredSidebarPreference = 'expanded' | 'collapsed';

interface PersistedSidebarState {
  sessionId: string;
  teamId: string;
  brandId: string;
  preference: StoredSidebarPreference;
}

const getDefaultState = (mode: CollapsedMode): SidebarState =>
  mode === 'rail' ? 'expanded' : 'hidden';

const getToggledState = (current: SidebarState, mode: CollapsedMode): SidebarState => {
  if (mode === 'hidden') {
    return current === 'expanded' ? 'hidden' : 'expanded';
  }

  return current === 'collapsed' ? 'expanded' : 'collapsed';
};

const toStoredPreference = (state: SidebarState): StoredSidebarPreference =>
  state === 'expanded' ? 'expanded' : 'collapsed';

const toSidebarState = (preference: StoredSidebarPreference, mode: CollapsedMode): SidebarState => {
  if (preference === 'expanded') {
    return 'expanded';
  }

  return mode === 'rail' ? 'collapsed' : 'hidden';
};

const getContextStamp = () => ({
  sessionId: storage.getSessionId() ?? '',
  teamId: storage.getTeamId() ?? '',
  brandId: storage.getBrandId() ?? '',
});

const persistSidebarPreference = (state: SidebarState) => {
  const stamp = getContextStamp();
  storage.setItem(StorageEnum.SIDEBAR_STATE, {
    ...stamp,
    preference: toStoredPreference(state),
  });
};

const getInitialSidebarState = (mode: CollapsedMode): SidebarState => {
  const stored = storage.getItem<PersistedSidebarState | undefined>(StorageEnum.SIDEBAR_STATE);
  const stamp = getContextStamp();

  const isSameContext =
    !!stored &&
    stored.sessionId === stamp.sessionId &&
    stored.teamId === stamp.teamId &&
    stored.brandId === stamp.brandId;

  if (isSameContext) {
    return toSidebarState(stored.preference, mode);
  }

  return getDefaultState(mode);
};

export const useSidebarState = ({ collapsedMode, isNested, contextKey }: UseSidebarStateParams) => {
  const [sidebarState, setSidebarState] = useState<SidebarState>(() =>
    getInitialSidebarState(collapsedMode),
  );
  const previousResetKey = useRef(`${collapsedMode}:${contextKey}`);
  const effectiveSidebarState: SidebarState = isNested ? 'expanded' : sidebarState;

  useEffect(() => {
    if (isNested) {
      setSidebarState('expanded');
    }
  }, [isNested]);

  useEffect(() => {
    const resetKey = `${collapsedMode}:${contextKey}`;
    if (previousResetKey.current === resetKey) {
      return;
    }

    previousResetKey.current = resetKey;
    const nextState = getDefaultState(collapsedMode);
    setSidebarState(nextState);
    persistSidebarPreference(nextState);
  }, [collapsedMode, contextKey]);

  const toggleSidebar = () => {
    const nextState = getToggledState(sidebarState, collapsedMode);
    setSidebarState(nextState);
    persistSidebarPreference(nextState);
  };

  return { effectiveSidebarState, toggleSidebar };
};
